class StoresSliderTicker extends HTMLElement {
  connectedCallback() {
    this.setup();
  }

  disconnectedCallback() {
    this.destroy();
  }

  setup() {
    this.destroy();

    this.track = this.querySelector(".stores-slider__track");
    this.group = this.querySelector(".stores-slider__group");

    if (!this.track || !this.group) return;

    this.direction = this.dataset.direction === "ticker-to-right" ? 1 : -1;
    this.pauseWhenAllVisible = this.dataset.pauseWhenAllVisible !== "false";
    this.delay = this.parseDelay();
    this.referenceWidth = this.parseReferenceWidth();
    this.offset = 0;
    this.isDragging = false;
    this.lastTime = null;
    this.groupWidth = 0;
    this.fits = false;
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onPointerMove = this.handlePointerMove.bind(this);
    this.onPointerUp = this.handlePointerUp.bind(this);
    this.onResize = this.handleResize.bind(this);

    this.addEventListener("pointerdown", this.onPointerDown);
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointercancel", this.onPointerUp);
    window.addEventListener("resize", this.onResize);

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.group);
    }

    this.syncMode();
    this.startAnimation();
  }

  destroy() {
    this.stopAnimation();

    this.removeEventListener("pointerdown", this.onPointerDown);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.onPointerUp);
    window.removeEventListener("resize", this.onResize);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    window.clearTimeout(this.resizeTimer);

    if (this.track) {
      this.track.style.transform = "";
      this.removeClones();
    }

    this.classList.remove("is-dragging", "stores-slider__ticker--fits");
    this.isDragging = false;
    this.lastTime = null;
    this.fits = false;
  }

  parseDelay() {
    const inlineDelay = parseFloat(this.style.getPropertyValue("--ticker-delay"));
    if (!Number.isNaN(inlineDelay) && inlineDelay > 0) return inlineDelay;

    const computedDelay = parseFloat(
      window.getComputedStyle(this).getPropertyValue("--ticker-delay")
    );

    return !Number.isNaN(computedDelay) && computedDelay > 0 ? computedDelay : 50;
  }

  /** Virtual width (px): speed = referenceWidth / delay → same px/s for any number of items */
  parseReferenceWidth() {
    const inline = parseFloat(this.style.getPropertyValue("--ticker-reference-width"));
    if (!Number.isNaN(inline) && inline > 0) return inline;

    const computed = parseFloat(
      window.getComputedStyle(this).getPropertyValue("--ticker-reference-width")
    );

    return !Number.isNaN(computed) && computed > 0 ? computed : 2000;
  }

  shouldFitAllVisible() {
    if (!this.pauseWhenAllVisible || !this.group) return false;

    this.groupWidth = this.group.offsetWidth;
    const containerWidth = this.clientWidth;

    return (
      this.groupWidth > 0 &&
      containerWidth > 0 &&
      this.groupWidth <= containerWidth + 1
    );
  }

  syncMode() {
    if (!this.track || !this.group) return;

    const shouldFit = this.shouldFitAllVisible();

    if (shouldFit) {
      if (!this.fits) {
        this.fits = true;
        this.classList.add("stores-slider__ticker--fits");
        this.isDragging = false;
        this.classList.remove("is-dragging");
        this.offset = 0;
        this.removeClones();
        this.track.style.transform = "";
        this.stopAnimation();
      }
      return;
    }

    if (this.fits) {
      this.fits = false;
      this.classList.remove("stores-slider__ticker--fits");
      this.offset = 0;
      this.startAnimation();
    }

    this.prepareClones();
    this.wrapOffset();
    this.applyTransform();
  }

  removeClones() {
    if (!this.track) return;

    this.track.querySelectorAll(".stores-slider__group--clone").forEach((node) => {
      node.remove();
    });
  }

  prepareClones() {
    if (this.fits) {
      this.removeClones();
      return;
    }

    this.removeClones();

    this.groupWidth = this.group.offsetWidth;

    if (this.groupWidth <= 0) return;

    const cloneCount = Math.max(2, Math.ceil(this.offsetWidth / this.groupWidth) + 1);

    for (let i = 0; i < cloneCount; i += 1) {
      const clone = this.group.cloneNode(true);
      clone.classList.add("stores-slider__group--clone");
      clone.setAttribute("aria-hidden", "true");
      this.track.appendChild(clone);
    }
  }

  handleResize() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      const previousOffset = this.offset;
      const wasFits = this.fits;

      this.syncMode();

      if (!wasFits && !this.fits && this.groupWidth > 0) {
        this.offset = previousOffset;
        this.wrapOffset();
        this.applyTransform();
      }
    }, 150);
  }

  startAnimation() {
    if (this.reducedMotion || this.fits || this.raf) return;

    this.lastTime = null;
    this.raf = window.requestAnimationFrame(this.tick.bind(this));
  }

  stopAnimation() {
    if (!this.raf) return;

    window.cancelAnimationFrame(this.raf);
    this.raf = null;
    this.lastTime = null;
  }

  tick(time) {
    if (this.fits) {
      this.stopAnimation();
      return;
    }

    if (this.lastTime == null) this.lastTime = time;

    const delta = (time - this.lastTime) / 1000;
    this.lastTime = time;

    if (!this.isDragging && this.groupWidth > 0) {
      const speed = this.referenceWidth / this.delay;
      this.offset += this.direction * speed * delta;
      this.wrapOffset();
      this.applyTransform();
    }

    this.raf = window.requestAnimationFrame(this.tick.bind(this));
  }

  wrapOffset() {
    if (this.groupWidth <= 0) return;

    while (this.offset <= -this.groupWidth) this.offset += this.groupWidth;
    while (this.offset > 0) this.offset -= this.groupWidth;
  }

  applyTransform() {
    if (this.fits) return;

    this.track.style.transform = `translate3d(${this.offset}px, 0, 0)`;
  }

  handlePointerDown(event) {
    if (this.fits) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartOffset = this.offset;
    this.classList.add("is-dragging");

    if (this.setPointerCapture) {
      this.setPointerCapture(event.pointerId);
    }
  }

  handlePointerMove(event) {
    if (!this.isDragging || this.fits) return;

    event.preventDefault();

    this.offset = this.dragStartOffset + (event.clientX - this.dragStartX);
    this.wrapOffset();
    this.applyTransform();
  }

  handlePointerUp(event) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.classList.remove("is-dragging");

    if (this.hasPointerCapture && this.hasPointerCapture(event.pointerId)) {
      this.releasePointerCapture(event.pointerId);
    }
  }
}

if (!customElements.get("stores-slider-ticker")) {
  customElements.define("stores-slider-ticker", StoresSliderTicker);
}
